﻿import * as metastoreApi from "../../shared/metastore-api-client";
import * as _ from "lodash";
import {
    LOAD_STORYLINE,
    SHOW_STORYLINE,
    SHOW_SNAPSHOT,
    ShowSnapshotAction,
    GO_TO_X_Y_Z,
    GO_TO_ID,
    PARAMETER_VALUE_UPDATED,
    DATASOURCE_UPDATED,
    DATASOURCE_ERROR_OCCURRED,
    UPDATE_CURRENT_SLIDE_TEMPLATE,
    UPDATE_SLIDE_TEMPLATES,
    UPDATE_CURRENT_FRAME_DATA,
    UPDATE_CANVAS_STATE,
    StorylineState,
    NavigationTarget,
    ADD_GLOBAL_DATASOURCE,
    UPDATE_INFLIGHT_REQUESTS_FOR_DATASOURCE,
    UpdateInFlightRequestsForDatasource,
    DatasourceErrorOccurredAction,
    DataSource,
    ShowStorylineAction,
    LoadStorylineAction,
    Storyline,
    DatasourceUpdatedAction,
    TemplateDefinition,
    TemplateUpdateOrigin,
    AddGlobalDatasource,
    GoToXYZAction
} from "./types";
import {
    TraceSource
} from "../tracing/types";
import { RootState } from "../index";
import { showError, showDetailedError, showSuccess } from "../notifications/actions";
import {
    buildSnapshotEnvelope,
    buildSnapshotLink,
    rebuildSnapshotMaps,
    snapshotContextFromMeta,
    SnapshotFormatError,
    validateSnapshotEnvelope
} from "./snapshots";
import { addTraceEvent } from "../tracing/actions";
import { animateSlideNavigation } from "../../shared/services/slideTransitionAnimationService";
import { BehaviorSubject, Observable, merge } from "rxjs";
import { map, filter, debounceTime, groupBy, mergeAll, scan, pairwise } from 'rxjs/operators';
import { MapWithPathSupport } from "../../shared/utilities";
import { sendCanvasDataToVsCode, sendCssClassesToVsCode, sendStorylineStateToVsCode } from "../vscode/actions";
import { setPageError } from "../app/actions";
import * as api from "../../shared/api-client";
import { AppError } from "../app/types";
import { jsonParseReviver } from "../../shared/api-client-extensions";

interface ApiRequestEvent {
    datasource: DataSource;
}

interface ApiResponseEvent {
    datasource: DataSource;
    payload: unknown;
}

interface ApiErrorEvent {
    datasource: api.DatasourceDisplayModel;
    error: any;
}

type ApiSuccessResponse = {
    type: "SUCCESS";
    payload: unknown;
}

type ApiErrorResponse = {
    type: "ERROR";
    error: any;
}

type ApiResponse = ApiSuccessResponse | ApiErrorResponse;

interface DatasourceStatusSummary {
    datasource: metastoreApi.Datasource;
    inFlightRequests: number;
    lastResponse: ApiResponse;
}

interface DatasourceStatusDictionary {
    [key: string]: DatasourceStatusSummary;
}

let datasourcesPendingRefresh: DataSource[] = [];
const datasourceRefreshRequests$ = new BehaviorSubject<string>(null);
const apiRequests$ = new BehaviorSubject<ApiRequestEvent>(null);
const apiResponses$ = new BehaviorSubject<ApiResponseEvent>(null);
const apiErrors$ = new BehaviorSubject<ApiErrorEvent>(null);
let datasourceStatuses$: Observable<DatasourceStatusDictionary>;

const datasourceCascadeMap = new Map<string, Array<string>>();

// Tracks why each datasource's most recent refresh was requested, so the DatasourceRefreshed trace can
// attribute it (initial load vs. parameter change vs. an explicit refreshDatasourceByName call).  Set at
// each refresh origin, keyed by datasource name; consumed (and cleared) when the refresh completes.
const datasourceRefreshReasons = new Map<string, TraceSource>();

export function initializeDatasourceFetchPipeline() {
    return async (dispatch, getState: () => RootState) => {
        // Debounce datasource refresh requests to eliminate unnecessary API calls...
        datasourceRefreshRequests$
            .pipe(
                filter(datasourceId => datasourceId !== undefined && datasourceId !== null),
                groupBy(datasourceId => datasourceId),
                map(group => group.pipe(
                    debounceTime(100)
                )),
                mergeAll()
            ).subscribe((datasourceId: string) => {
                const { datasources } = getState().storyline;
                const datasource = datasources.get(datasourceId);

                refreshDatasource(datasource, true)(dispatch, getState);
            });

        datasourceStatuses$ =
            merge(
                apiRequests$.pipe(filter(r => !!r), map(r => ({ "type": "REQUEST", ...r }))),
                apiResponses$.pipe(filter(r => !!r), map(r => ({ "type": "RESPONSE", ...r }))),
                apiErrors$.pipe(filter(r => !!r), map(r => ({ "type": "ERROR", ...r })))
            )
                .pipe(
                    scan((acc, elem) => {
                        switch (elem.type) {
                            case "REQUEST":
                                return (() => {
                                    const request = elem as ApiRequestEvent;
                                    var currentStatus = acc[request.datasource.name] || { inFlightRequests: 0, lastResponse: null };

                                    dispatch({
                                        type: UPDATE_INFLIGHT_REQUESTS_FOR_DATASOURCE,
                                        datasource: request.datasource,
                                        inFlightRequestCount: currentStatus.inFlightRequests + 1
                                    } as UpdateInFlightRequestsForDatasource);

                                    return {
                                        ...acc,
                                        [request.datasource.name]: {
                                            datasource: request.datasource,
                                            inFlightRequests: currentStatus.inFlightRequests + 1
                                        }
                                    } as DatasourceStatusDictionary;
                                })();

                            case "RESPONSE":
                                return (() => {
                                    const response = elem as ApiResponseEvent;
                                    var currentStatus = acc[response.datasource.name];

                                    dispatch({
                                        type: UPDATE_INFLIGHT_REQUESTS_FOR_DATASOURCE,
                                        datasource: response.datasource,
                                        inFlightRequestCount: currentStatus.inFlightRequests - 1
                                    } as UpdateInFlightRequestsForDatasource);

                                    return {
                                        ...acc,
                                        [response.datasource.name]: {
                                            datasource: response.datasource,
                                            inFlightRequests: currentStatus.inFlightRequests - 1,
                                            lastResponse: { type: "SUCCESS", payload: response.payload }
                                        }
                                    } as DatasourceStatusDictionary;
                                })();

                            case "ERROR":
                                return (() => {
                                    const error = elem as ApiErrorEvent;
                                    const currentStatus = acc[error.datasource.name];

                                    dispatch({
                                        type: UPDATE_INFLIGHT_REQUESTS_FOR_DATASOURCE,
                                        datasource: error.datasource,
                                        inFlightRequestCount: currentStatus.inFlightRequests - 1
                                    } as UpdateInFlightRequestsForDatasource);

                                    return {
                                        ...acc,
                                        [error.datasource.name]: {
                                            datasource: error.datasource,
                                            inFlightRequests: currentStatus.inFlightRequests - 1,
                                            lastResponse: { type: "ERROR", error: error.error }
                                        }
                                    } as DatasourceStatusDictionary;
                                })();
                        }
                    }, {} as DatasourceStatusDictionary)
                );

        // Monitor data source event stream for final outstanding requests (per datasource) completing and
        // dispatch the DATASOURCE_UPDATED action if necessary...
        datasourceStatuses$
            .pipe(pairwise())
            .subscribe(([prevState, currentState]) => {
                for (let datasourceId of Object.keys(currentState)) {
                    const s0 = prevState[datasourceId];
                    const s1 = currentState[datasourceId];

                    // Only update the datasource value if the last in-flight request has just completed...
                    if (!s0 || s0.inFlightRequests !== 1 || s1.inFlightRequests !== 0) continue;

                    const hasCascadingInFlightCall = (datasourceId: string) => {
                        const cascadeSources = datasourceCascadeMap.get(datasourceId) || [];

                        if (cascadeSources && cascadeSources.length) {
                            return cascadeSources.find(hasCascadingInFlightCall);
                        }

                        return currentState[datasourceId].inFlightRequests > 0;
                    }

                    // If any of the cascading parent datasources have outstanding responses, don't bother updating the frame data with this (soon to be overwritten) result...
                    if (hasCascadingInFlightCall(datasourceId) && !_.isEmpty(getState().storyline.datasourceValues.get(datasourceId)?.[0])) continue;

                    const refreshReason = datasourceRefreshReasons.get(datasourceId) ?? { kind: "ParameterValueChanged" };
                    datasourceRefreshReasons.delete(datasourceId);

                    dispatch(addTraceEvent({
                        kind: "DatasourceRefreshed",
                        name: s0.datasource.name,
                        oldValue: getState().storyline.datasourceValues.get(datasourceId),
                        newValue: s1.lastResponse,
                        source: refreshReason as TraceSource
                    }));

                    switch (s1.lastResponse.type) {
                        case "SUCCESS":
                            dispatch({
                                type: DATASOURCE_UPDATED,
                                datasourceName: datasourceId,
                                data: s1.lastResponse.payload
                            } as DatasourceUpdatedAction);
                            break;

                        case "ERROR":
                            dispatch(dataSourceErrorOccurred(s1.datasource.name, s1.lastResponse.error));
                            dispatch(showDetailedError(s1.lastResponse.error.message, s1.lastResponse.error.details, { preventDuplicates: true }));
                            break;
                    }
                }
            });

        // Generate a new Q-Trace-ID value after 2000ms of radio silence has occurred...
        datasourceStatuses$
            .pipe(map(s => Object.values(s ?? {}).reduce((acc, elem) => acc + elem.inFlightRequests, 0))) // Calculate the total in-flight request count..
            .pipe(debounceTime(2000)) // Wait for at least 2000ms without any new requests/responses...
            .pipe(filter(inFlightRequests => inFlightRequests === 0)) // Ensure that all in-flight requests have completed...
            .subscribe(() => { metastoreApi.generateNewTraceId(); });
    }
}

const getErrorDetailsForDeveloper = (ex, additionalInformation: Record<string, string> = {}): string => {
    const result = [];

    if (ex.response.includes("ERR_NGROK_")) {
        result.push("Failed to reach the Data API (via **ngrok tunnel**).");
        result.push("Please ensure that your **local SDK** is running and that the **ngrok tunnel** is active.");
    }
    else if (ex.response.includes("DbzError")) {
        result.push("Please verify that this resource exists within the current branch.");
    }
    else {
        result.push(ex.response);
    }

    result.push(
        Object.entries(additionalInformation)
            .map(([key, value]) => `<strong>${key}:</strong> <code>${value}</code>`)
            .join("<br />")
    );

    if (ex["Q-Trace-Id"]) {
        result.push(`<br /><strong>Trace ID:</strong> <code>${ex["Q-Trace-Id"]}</code>`);
    }

    return result.filter(Boolean).join("<br />");
};

function fetchDatasourceData(datasource: DataSource, parameterValues: Map<string, any>, datasources: DataSource[], compressRequestBody = false) {
    return async (dispatch, getState: () => RootState) => {
        if (!datasource.apiEndpoint) return null;

        // Short-circuit if we're missing any required parameters...
        if (datasource.parameters.filter(p => p.isRequired).map(p => parameterValues.get(p.name)).some(pv => pv === undefined || pv === null)) return null;

        const { user } = getState().app;

        // Get the parameter values and construct the parameter object for the request body...
        let parameterObject = _.chain(datasource.parameters)
            .map(p => ({ key: p.name, value: parameterValues.get(p.name) ?? p.defaultValue }))
            .filter(p => p.value !== null && p.value !== undefined)
            .reduce((acc, elem) => { acc[elem.key] = elem.value; return acc; }, {})
            .value();

        const appState = getState().app;
        const { metastoreUrl, dynamicDataApiUrl, maxDatasourceRetryCount, dynamicDataApiBypassesMetastore } = appState.settings;
        const activeBranch = appState.activeBranch;

        apiRequests$.next({ datasource });
        const result = await new metastoreApi.DatasourcesClient(metastoreUrl, activeBranch, maxDatasourceRetryCount, dynamicDataApiBypassesMetastore).fetchData(dynamicDataApiUrl, (datasource.apiEndpoint ?? []).join("/"), parameterObject, compressRequestBody).catch(ex => {
            const getError = (ex): AppError => {
                if (ex.status == null)
                    return { type: "Connectivity", message: `Your browser cannot reach the Data Server.`, details: `Unable to contact the server at \`${dynamicDataApiBypassesMetastore ? dynamicDataApiUrl : metastoreUrl}\`.<br />Please ensure you are connected to your corporate network or VPN.<br />If the issue persists, please contact the site administrator.${ex["Q-Trace-Id"] ? "<br /><br /><strong>Trace ID:</strong> <code>" + ex["Q-Trace-Id"] : "</code>"}` };

                if (ex.status === 404)
                    return { type: "NotFound", message: "Datasource not found.", details: `Please refresh this page and try again.  If the issue persists, please contact the site administrator.` };

                if (ex.status === 429)
                    return { type: "Connectivity", message: "Rate limit exceeded.", details: `Too many requests were submitted within the last minute.<br />Please wait a moment and try again.` };

                return { type: "Generic", message: "Failed to fetch page data.", details: user.isDeveloper ? getErrorDetailsForDeveloper(ex) : ex.response ?? "Please check the filter values and try again." };
            };

            const error = getError(ex);
            apiErrors$.next({ datasource, error });

            throw ex;
        });
        apiResponses$.next({ datasource, payload: result });

        const storyline = getState().storyline;
        if (result != null) {
            const affectedSlots = storyline?.storyline?.chapters?.flatMap?.(c =>
                c.pages.flatMap(p =>
                    p.paragraphs.flatMap(par =>
                        par.slots
                    )
                )
            )?.filter(slot => slot.dataSource === datasource.name) ?? [];

            const allParameters = _.chain(datasources).flatMap(ds => ds.parameters).map(p => p.name).uniq().value();
            const affectedParameters = _.intersection(affectedSlots.map(s => s.name), allParameters);
            for (let affectedParameter of affectedParameters) {
                updateParameterValue(affectedParameter, result, false, false, datasource.name)(dispatch, getState);
            }
        }

        return result;
    }
}

function dataSourceErrorOccurred(datasourceName: string, error: any) {
    return async (dispatch, _getState: () => RootState) => {
        dispatch(addTraceEvent({
            kind: "DatasourceErrorOccurred",
            name: datasourceName,
            error: error,
            source: { kind: "DatasourceBinding", datasourceName }
        }));

        dispatch({
            type: DATASOURCE_ERROR_OCCURRED,
            datasourceName,
            error: { ...error, timestamp: new Date() }
        } as DatasourceErrorOccurredAction);
    };
}

function extractDatasourcesFromObject(object: object): DataSource[] {
    if (!object) return [];
    return Object.entries(object).map(([key, value]: [string, metastoreApi.Datasource]) => ({
        name: key,
        autoRefresh: value?.autoRefresh ?? true,
        parameters: [
            ...value?.requiredParameters?.map(p => ({ ...p, isRequired: true })) ?? [],
            ...value?.optionalParameters?.map(p => ({ ...p, isRequired: false })) ?? []
        ],
        defaultData: JSON.parse(value?.defaultData ?? "null", jsonParseReviver),
        apiEndpoint: value?.apiEndpoint
    } as DataSource));
}

function mapDatasourcesForObject(object: object): { [name: string]: DataSource } {
    const datasources = extractDatasourcesFromObject(object);
    return Object.fromEntries(datasources.map(ds => [ds.name, ds]));
}

function* getAllDatasources(storyline: metastoreApi.Storyline) {
    yield* extractDatasourcesFromObject(storyline.dataSources);

    for (let chapter of storyline.chapters) {
        yield* extractDatasourcesFromObject(chapter.dataSources);

        for (let page of chapter.pages) {
            yield* extractDatasourcesFromObject(page.dataSources);

            for (let paragraph of page.paragraphs) {
                yield* extractDatasourcesFromObject(paragraph.dataSources);
            }
        }
    }
}

function mapApiStorylineToStateModel(storyline: metastoreApi.Storyline): Storyline {
    // Backwards compatibility for older version of Metastore.
    // @todo: Remove this once all Metastore instances are updated.
    let templates: Record<string, TemplateDefinition> = storyline.templates ??
        Object.fromEntries(
            _.chain(storyline.chapters)
                .flatMap(c => c.pages.map(p => p.template))
                .uniqBy((t: metastoreApi.Template) => t.name)
                .map((t: metastoreApi.Template) => [t.name, t.root])
                .value()
        );
    templates = _.mapValues(templates, t => ({ ...t, version: 0 }));

    return {
        ...storyline,
        templates,
        dataSources: mapDatasourcesForObject(storyline.dataSources),
        chapters: storyline.chapters.map(chapter => ({
            ...chapter,
            dataSources: mapDatasourcesForObject(chapter.dataSources),
            pages: chapter.pages.map(page => ({
                ...page,
                template: typeof page.template == "string" ? page.template : page.template?.name, // Backwards compatibility for older version of Metastore.  @todo: Remove this once all Metastore instances are updated.
                dataSources: mapDatasourcesForObject(page.dataSources),
                paragraphs: page.paragraphs.map(paragraph => ({
                    ...paragraph,
                    dataSources: mapDatasourcesForObject(paragraph.dataSources)
                }))
            }))
        }))
    } as Storyline;
}

// The delay gives the post-load datasource fan-out (or snapshot restore) a chance to settle
// before the editor receives its state sync...
function scheduleVsCodeSync(dispatch) {
    setTimeout(() => {
        dispatch(sendStorylineStateToVsCode());
        dispatch(sendCanvasDataToVsCode());
        sendCssClassesToVsCode();
    }, 2000);
}

export function loadStoryline(id: string, parameterValues: Map<string, any>) {
    return async (dispatch, getState: () => RootState) => {
        dispatch({
            type: LOAD_STORYLINE,
            id
        } as LoadStorylineAction);

        datasourceCascadeMap.clear();

        const appState = getState().app;
        const metastoreUrl = appState.settings.metastoreUrl;
        const activeBranch = appState.activeBranch;
        const user = appState.user;

        const storyline = await new metastoreApi.StorylinesClient(metastoreUrl, activeBranch).getById(id).catch(ex => {
            const getError = (ex): AppError => {
                if (ex.status == null)
                    return { type: "Connectivity", message: `Your browser cannot reach the Data Server.`, details: `Unable to contact the server at \`${metastoreUrl}\`.<br />Please ensure you are connected to your corporate network or VPN.<br />If the issue persists, please contact the site administrator.${ex["Q-Trace-Id"] ? "<br /><br /><strong>Trace ID:</strong> <code>" + ex["Q-Trace-Id"] : "</code>"}` };

                if (ex.status === 404)
                    return { type: "NotFound", message: "Storyline not found.", details: `Please check the address and try again.<br />If you believe this is an error, please contact the site administrator.` };

                if (ex.status === 429)
                    return { type: "Connectivity", message: "Rate limit exceeded.", details: `Too many requests were submitted within the last minute.<br />Please wait a moment and try again.` };

                return { type: "Generic", message: "Failed to load storyline from the Data Server.", details: user.isDeveloper ? getErrorDetailsForDeveloper(ex, { "Branch": activeBranch, "Storyline": id }) : "Please check the URL or try a different link from the menu." };
            };

            dispatch(setPageError(getError(ex)));
            return;
        });

        if (!storyline) return;

        const datasources = [...getAllDatasources(storyline)];
        const datasourceMap = new Map(datasources.map(ds => [ds.name, ds]));
        const datasourceValues = new Map(datasources.filter(ds => ds.defaultData !== null && ds.defaultData !== undefined).map(ds => [ds.name, ds.defaultData]));

        // User has already navigated to a different storyline - discard the results and move on...
        if (getState().storyline.id !== id) {
            return;
        }

        const mappedStoryline = mapApiStorylineToStateModel(storyline);

        // Populate the unbound parameter values which are found in the default data...
        const allParameters = _.chain(datasources).flatMap(ds => ds.parameters).map(p => p.name).uniq().value();
        const unboundParameters = _.difference(allParameters, ...parameterValues.keys());
        const slotsInFirstParagraph = mappedStoryline?.chapters?.[0]?.pages?.[0]?.paragraphs?.[0]?.slots;
        const parametersToPopulate = _.intersection(slotsInFirstParagraph.map(s => s.name), unboundParameters);
        slotsInFirstParagraph
            .filter(slot => parametersToPopulate.includes(slot.name))
            .forEach(slot => {
                if (datasourceValues.has(slot.dataSource)) {
                    parameterValues.set(slot.name, datasourceValues.get(slot.dataSource));
                }
            });

        dispatch({
            type: SHOW_STORYLINE,
            id,
            storyline: mappedStoryline,
            dataSources: datasourceMap,
            dataSourceValues: datasourceValues,
            parameterValues
        } as ShowStorylineAction);

        // Fire off all initial datasource requests...
        datasources.filter(ds => ds.autoRefresh).forEach(ds => {
            datasourceRefreshReasons.set(ds.name, { kind: "InitialLoad" });
            datasourceRefreshRequests$.next(ds.name);
        });

        scheduleVsCodeSync(dispatch);
    }
}

export function captureSnapshot(options: { name?: string; sharedWith: string[] }) {
    return async (dispatch, getState: () => RootState) => {
        const state = getState();
        const { metastoreUrl } = state.app.settings;

        const envelope = buildSnapshotEnvelope(state.storyline, {
            name: options.name,
            sharedWith: options.sharedWith ?? [],
            capturedUrl: `${window.location.pathname}${window.location.search}${window.location.hash}`,
            branch: state.app.activeBranch
        });

        try {
            const { id, owner } = await new metastoreApi.SnapshotsClient(metastoreUrl).create(envelope);
            const link = buildSnapshotLink(envelope.meta.storylineName, owner, id, window.location.origin);

            // Clipboard access can be denied (e.g. missing permissions) - the snapshot is saved either way...
            const copied = await navigator.clipboard?.writeText(link).then(() => true).catch(() => false);
            dispatch(showSuccess(copied ? "Snapshot saved — the share link has been copied to your clipboard." : "Snapshot saved."));

            return { id, owner, link };
        }
        catch (ex) {
            dispatch(showDetailedError("Failed to save the snapshot.", ex?.response ?? ex?.message ?? ""));
            return null;
        }
    };
}

export function loadSnapshot(owner: string, snapshotId: string) {
    return async (dispatch, getState: () => RootState) => {
        const sentinelId = `snapshot:${owner}/${snapshotId}`;
        dispatch({
            type: LOAD_STORYLINE,
            id: sentinelId
        } as LoadStorylineAction);

        datasourceCascadeMap.clear();

        const appState = getState().app;
        const metastoreUrl = appState.settings.metastoreUrl;
        const user = appState.user;

        const getError = (ex): AppError => {
            if (ex instanceof SnapshotFormatError)
                return { type: "Generic", message: "This snapshot cannot be opened.", details: ex.message };

            if (ex.status == null)
                return { type: "Connectivity", message: `Your browser cannot reach the Data Server.`, details: `Unable to contact the server at \`${metastoreUrl}\`.<br />Please ensure you are connected to your corporate network or VPN.<br />If the issue persists, please contact the site administrator.${ex["Q-Trace-Id"] ? "<br /><br /><strong>Trace ID:</strong> <code>" + ex["Q-Trace-Id"] : "</code>"}` };

            if (ex.status === 403)
                return { type: "Generic", message: "This snapshot has not been shared with you.", details: `Ask the snapshot's owner to share it with you, then try again.` };

            if (ex.status === 404)
                return { type: "NotFound", message: "Snapshot not found.", details: `The snapshot may have been deleted by its owner.<br />Please check the address and try again.` };

            return { type: "Generic", message: "Failed to load the snapshot.", details: user.isDeveloper ? getErrorDetailsForDeveloper(ex, { "Snapshot": `${owner}/${snapshotId}` }) : "Please check the URL or try a different link." };
        };

        let meta, data;
        try {
            const envelope = await new metastoreApi.SnapshotsClient(metastoreUrl).getById(owner, snapshotId);
            ({ meta, data } = validateSnapshotEnvelope(envelope));
        }
        catch (ex) {
            dispatch(setPageError(getError(ex)));
            return;
        }

        if (getState().storyline.id !== sentinelId) return;

        const { datasources, datasourceValues, parameterValues } = rebuildSnapshotMaps(data);

        dispatch({
            type: SHOW_SNAPSHOT,
            storyline: data.storyline,
            dataSources: datasources,
            dataSourceValues: datasourceValues,
            parameterValues,
            canvasState: data.canvasState ?? {},
            position: data.position ?? { chapterIndex: 0, pageIndex: 0, paragraphIndex: 0 },
            snapshot: snapshotContextFromMeta(meta)
        } as ShowSnapshotAction);

        scheduleVsCodeSync(dispatch);
    };
}

export function updateParameterValue(parameterName: string, newValueOrTransformer: ((oldValue: unknown) => unknown) | unknown, isInitialLoad = false, preventDatasourceRefresh = false, sourceDatasourceId: string = null, traceSourceKind: "InputControlChange" | "UpdateParameterValueCall" = "InputControlChange") {
    return (dispatch, getState: () => RootState) => {
        if (!parameterName) return;

        const oldValue = getState()?.storyline?.parameterValues?.get(parameterName);
        const newValue = _.isFunction(newValueOrTransformer) ? newValueOrTransformer(oldValue) : newValueOrTransformer;

        // Short-circuit action if the value hasn't actually changed...
        if (_.isEqual(oldValue, newValue)) {
            return;
        }

        dispatch(addTraceEvent({
            kind: "ParameterValueChanged",
            name: parameterName,
            oldValue,
            newValue,
            source: { kind: isInitialLoad ? "InitialLoad" : sourceDatasourceId ? "DatasourceBinding" : traceSourceKind } as TraceSource
        }));

        dispatch({
            type: PARAMETER_VALUE_UPDATED,
            parameterName,
            newValue
        });

        // Datasources will only reference the root parameter of this (possible nested) path...
        const rootParameterName = MapWithPathSupport.getBaseParameterName(parameterName);

        // Find the data sources that are affected by the parameter change...
        let affectedDatasources = _.filter(Array.from(getState().storyline.datasources.values()), (ds: DataSource) => !!_.find(ds.parameters, p => p.name === rootParameterName));

        // Record the datasource that triggered this cascading update...
        if (sourceDatasourceId) {
            affectedDatasources.forEach(ds => {
                datasourceCascadeMap.set(ds.name, _.uniq(_.filter([...datasourceCascadeMap.get(ds.name) || [], sourceDatasourceId], a => !!a)));
            });
        }

        // In snapshot mode the captured frame data is the point of the feature - parameters may still
        // drive pure-UI template state, but this must not schedule refetches that would overwrite it...
        if (!getState().storyline.snapshot) {
            // Keep track of the data sources that now have outstanding updates pending, so that we can refresh them on the next manual update run...
            let dataSourcesToManuallyRefresh = _.filter(affectedDatasources, ds => !ds.autoRefresh);
            datasourcesPendingRefresh = _.unionBy(datasourcesPendingRefresh, dataSourcesToManuallyRefresh, a => a.name);
            dataSourcesToManuallyRefresh.forEach(ds => datasourceRefreshReasons.set(ds.name, { kind: "ParameterValueChanged" }));

            if (!preventDatasourceRefresh) {
                // Post the list of data sources to update to the RxJS subject, so that datasource updates can be grouped and debounced...
                let datasourcesToAutoRefresh = _.filter(affectedDatasources, ds => ds.autoRefresh);
                for (let datasource of datasourcesToAutoRefresh) {
                    datasourceRefreshReasons.set(datasource.name, { kind: "ParameterValueChanged" });
                    datasourceRefreshRequests$.next(datasource.name);
                }
            }
        }

        setTimeout(() => dispatch(sendCanvasDataToVsCode()), 500);
    };
}

export function setDefaultParameterValue(parameterName: string, newValue: any) {
    return async (dispatch, getState: () => RootState) => {
        if (!parameterName) return;

        // Short-circuit action if the parameter already has a value...
        const existingValue = getState()?.storyline?.parameterValues?.get(parameterName);
        if (existingValue === null || existingValue === undefined) {
            updateParameterValue(parameterName, newValue)(dispatch, getState);
        }
    };
}

export function refreshDatasourceByName(name: string, _additionalInputParameters = {}, _mapResultToDatasourceParameters = false, compressRequestBody = false) {
    const additionalParameters = _additionalInputParameters ?? {};
    const mapResultToDatasourceParameters = _additionalInputParameters === true ? true : (_mapResultToDatasourceParameters ?? false); // Backwards compatibility for prior version, where "mapResultToDatasourceParameters" was the second parameter..."

    return async (dispatch, getState: () => RootState) => {
        const datasource = _.find(Array.from(getState().storyline.datasources).map(([_, value]) => value), a => a.name === name);

        if (datasource) {
            // Every refreshDatasourceByName invocation is a manual/template-driven refresh (initial load and
            // parameter-change refreshes go through the request subject / refreshDatasource directly).
            datasourceRefreshReasons.set(datasource.name, { kind: "RefreshDatasourceByNameCall" });
            refreshDatasource(datasource, mapResultToDatasourceParameters, additionalParameters, compressRequestBody)(dispatch, getState);
        }
    };
}

export function refreshDatasource(datasource: DataSource, mapResultToDatasourceParameters = false, additionalParameters = {}, compressRequestBody = false) {
    return async (dispatch, getState: () => RootState) => {
        if (!datasource) return;

        // Snapshot mode is frozen: this is the single point through which every state-mutating
        // datasource fetch flows (debounced pipeline, refreshDatasourceByName, applyParameterValueChanges)...
        if (getState().storyline.snapshot) return;

        // If additional parameters are provided, augment the canvas parameter values with these...
        const parameterValues = _.isEmpty(additionalParameters) ?
            getState().storyline.parameterValues :
            new Map<string, any>([
                ...getState().storyline.parameterValues.entries(),
                ...Object.entries(additionalParameters)
            ]);

        // Short-circuit if we're missing any required parameters...
        if (datasource.parameters.filter(p => p.isRequired).map(p => parameterValues.get(p.name)).some(pv => pv === undefined || pv === null)) return;

        const datasourcesToMapParametersTo = mapResultToDatasourceParameters ? Array.from(getState().storyline.datasources.values()) : [];
        await fetchDatasourceData(datasource, parameterValues, datasourcesToMapParametersTo, compressRequestBody)(dispatch, getState);

        setTimeout(() => dispatch(sendCanvasDataToVsCode()), 500);
    };
}

export function fetchDatasourceByName(name: string, _additionalInputParameters = {}, compressRequestBody = false) {
    const additionalParameters = _additionalInputParameters ?? {};

    return async (_dispatch, getState: () => RootState) => {
        const datasource = _.find(Array.from(getState().storyline.datasources).map(([_, value]) => value), a => a.name === name);

        if (!datasource)
            throw new Error(`Datasource with name '${name}' not found in the current storyline.`);

        // If additional parameters are provided, augment the canvas parameter values with these...
        const parameterValues = _.isEmpty(additionalParameters) ?
            getState().storyline.parameterValues :
            new Map<string, any>([
                ...getState().storyline.parameterValues.entries(),
                ...Object.entries(additionalParameters)
            ]);

        const missingInputParameters = _.chain(datasource.parameters).filter(p => p.isRequired).map(p => p.name).filter(p => parameterValues.get(p) === undefined || parameterValues.get(p) === null).value();
        if (missingInputParameters.length > 0)
            throw new Error(`The following required parameters are missing values: ${missingInputParameters.join(", ")}`);

        // Get the parameter values and construct the parameter object for the request body...
        const inputParameters = Object.fromEntries(
            datasource.parameters
                .map(p => [p.name, parameterValues.get(p.name)])
                .filter(([_key, value]) => value !== null && value !== undefined)
        );

        const appState = getState().app;
        const { metastoreUrl, dynamicDataApiUrl, dynamicDataApiBypassesMetastore } = appState.settings;
        const activeBranch = appState.activeBranch;
        return new metastoreApi.DatasourcesClient(metastoreUrl, activeBranch, 0, dynamicDataApiBypassesMetastore)
            .fetchData(dynamicDataApiUrl, (datasource.apiEndpoint ?? []).join("/"), inputParameters, compressRequestBody);
    };
};

export function applyParameterValueChanges(mapResultToDatasourceParameters = false) {
    return async (dispatch, getState: () => RootState) => {
        // Make a copy of the datasourcesPendingRefresh array and clear out the original to prevent race conditions/duplicate API calls on subsequent function invocations...
        const snapshotDatasourcesPendingRefresh = [...datasourcesPendingRefresh];
        datasourcesPendingRefresh = [];

        await Promise.all(_.map(snapshotDatasourcesPendingRefresh, async ds => await refreshDatasource(ds, mapResultToDatasourceParameters)(dispatch, getState)));
    }
}

type CoordinateCalculators = {
    [key: string]: (state: StorylineState) => NavigationTarget
}

const coordinateCalculators: CoordinateCalculators = {
    "NEXT_PARAGRAPH": (state) => {
        if (state.paragraphIndex < state.canvases[state.chapterIndex][state.pageIndex].length - 1) {
            return {
                chapterIndex: state.chapterIndex,
                pageIndex: state.pageIndex,
                paragraphIndex: state.paragraphIndex + 1,
                previousFrameIndex: state.paragraphIndex
            };
        }
        else if (state.pageIndex < state.canvases[state.chapterIndex].length - 1) {
            return {
                chapterIndex: state.chapterIndex,
                pageIndex: state.pageIndex + 1,
                paragraphIndex: 0
            }
        }
        else if (state.chapterIndex < state.canvases.length - 1) {
            return {
                chapterIndex: state.chapterIndex + 1,
                pageIndex: 0,
                paragraphIndex: 0
            }
        }
    },
    "PREVIOUS_PARAGRAPH": (state) => {
        if (state.paragraphIndex > 0) {
            const paragraphIndex = state.paragraphIndex - 1;

            return {
                chapterIndex: state.chapterIndex,
                pageIndex: state.pageIndex,
                paragraphIndex,
                previousFrameIndex: state.paragraphIndex
            };
        }
        else if (state.pageIndex > 0) {
            const pageIndex = state.pageIndex - 1;
            const paragraphIndex = state.canvases[state.chapterIndex][pageIndex].length - 1;

            return {
                chapterIndex: state.chapterIndex,
                pageIndex,
                paragraphIndex
            }
        }
        else if (state.chapterIndex > 0) {
            const chapterIndex = state.chapterIndex - 1;
            const pageIndex = state.canvases[chapterIndex].length - 1;
            const paragraphIndex = state.canvases[chapterIndex][pageIndex].length - 1;

            return {
                chapterIndex,
                pageIndex,
                paragraphIndex
            }
        }
    },
    "NEXT_PAGE": (state) => {
        if (state.pageIndex < state.canvases[state.chapterIndex].length - 1) {
            return {
                chapterIndex: state.chapterIndex,
                pageIndex: state.pageIndex + 1,
                paragraphIndex: 0
            }
        }
        else if (state.chapterIndex < state.canvases.length - 1) {
            return {
                chapterIndex: state.chapterIndex + 1,
                pageIndex: 0,
                paragraphIndex: 0
            }
        }
    },
    "PREVIOUS_PAGE": (state) => {
        if (state.pageIndex > 0) {
            return {
                chapterIndex: state.chapterIndex,
                pageIndex: state.pageIndex - 1,
                paragraphIndex: 0
            }
        }
        else if (state.chapterIndex > 0) {
            const chapterIndex = state.chapterIndex - 1;
            const pageIndex = state.canvases[chapterIndex].length - 1;

            return {
                chapterIndex,
                pageIndex,
                paragraphIndex: 0
            }
        }
    },
    "NEXT_CHAPTER": (state) => {
        if (state.chapterIndex < state.canvases.length - 1) {
            return {
                chapterIndex: state.chapterIndex + 1,
                pageIndex: 0,
                paragraphIndex: 0
            }
        }
    },
    "PREVIOUS_CHAPTER": (state) => {
        if (state.chapterIndex > 0) {
            return {
                chapterIndex: state.chapterIndex - 1,
                pageIndex: 0,
                paragraphIndex: 0
            }
        }
    }
};

export function navigateTo(direction: "NEXT_PARAGRAPH" | "PREVIOUS_PARAGRAPH" | "NEXT_PAGE" | "PREVIOUS_PAGE" | "NEXT_CHAPTER" | "PREVIOUS_CHAPTER") {
    return (dispatch, getState: () => RootState) => {
        const storyline = getState()?.storyline;
        if (!storyline) return;

        const handler = coordinateCalculators[direction];
        const newCoordinates = handler(storyline);
        if (newCoordinates) {
            goToXYZ(newCoordinates.chapterIndex, newCoordinates.pageIndex, newCoordinates.paragraphIndex, newCoordinates.previousParagraphIndex)(dispatch, getState);
        }

        setTimeout(() => {
            dispatch(sendStorylineStateToVsCode());
        }, 500);
    }
}

export function goToXYZ(chapterIndex: number, pageIndex: number, paragraphIndex: number, previousParagraphIndex?: number) {
    return (dispatch, getState: () => RootState) => {
        const storyline = getState()?.storyline;
        if (!storyline) return;

        const animationDirection = chapterIndex > storyline.chapterIndex ? "right" :
            chapterIndex < storyline.chapterIndex ? "left" :
                pageIndex > storyline.pageIndex ? "down" :
                    pageIndex < storyline.pageIndex ? "up" :
                        null;

        animationDirection && animateSlideNavigation(animationDirection);

        dispatch({
            type: GO_TO_X_Y_Z,
            chapterIndex,
            pageIndex,
            paragraphIndex,
            previousParagraphIndex
        } as GoToXYZAction);

        setTimeout(() => {
            dispatch(sendStorylineStateToVsCode());
        }, 500);
    }
}

export function goToID(id: string) {
    return {
        type: GO_TO_ID,
        id
    }
}

export function updateCurrentSlideTemplate(newTemplate: string, newCustomCss: string, origin: TemplateUpdateOrigin = "DevTools") {
    setTimeout(sendCssClassesToVsCode, 2000);

    return {
        type: UPDATE_CURRENT_SLIDE_TEMPLATE,
        newTemplate,
        newCustomCss,
        origin
    }
}

export function updateSlideTemplates(newTemplates: Record<string, TemplateDefinition>, origin: TemplateUpdateOrigin = "DevTools") {
    return {
        type: UPDATE_SLIDE_TEMPLATES,
        newTemplates,
        origin
    }
}

export function updateCurrentFrameData(newData: any) {
    return {
        type: UPDATE_CURRENT_FRAME_DATA,
        newData
    }
}

export function updateCurrentCanvasState(stateChunk: Object) {
    return (dispatch, getState: () => RootState) => {
        const currentState = getState()?.storyline.canvasState;
        // Short-circuit if the values are unchanged...
        if (_.isMatch(currentState, stateChunk)) return;

        const newState = { ...currentState, ...stateChunk };

        dispatch(addTraceEvent({
            kind: "CanvasStateChanged",
            name: Object.keys(stateChunk).join(", "),
            oldValue: currentState,
            newValue: newState,
            source: { kind: "ManualUpdate" } as TraceSource
        }));

        dispatch({
            type: UPDATE_CANVAS_STATE,
            newState
        });
    };
}

function getTemplateReferences(jsx: string): string[] {
    const regex = /<Template[^>]*path="([^"]*)"/g;
    return _.uniq(Array.from(jsx.matchAll(regex)).map(match => match[1]));
}

/// Build up hierarchical input model from flat template dictionary...
function getTemplateInputModel(templateName: string, templates: Record<string, TemplateDefinition>, currentHierarchy: string[] = []): metastoreApi.TemplateInputModel {
    const template = templates[templateName];
    // Short-circuit if template not found or a circular reference is detected...
    if (!template || currentHierarchy.includes(templateName)) {
        return undefined;
    }

    const templateReferences = getTemplateReferences(template.jsx);

    return {
        id: templateName,
        jsx: template.jsx,
        css: template.css ?? "",
        includes: templateReferences.map(tr => getTemplateInputModel(tr, templates, [...currentHierarchy, templateName]))
    };
}

export function saveCurrentPageTemplates(templates: Record<string, TemplateDefinition>) {
    return (dispatch, getState: () => RootState) => {
        const storylineState = getState().storyline;

        const appState = getState().app;
        const metastoreUrl = appState.settings.metastoreUrl;
        const activeBranch = appState.activeBranch;

        new metastoreApi.StorylinesClient(metastoreUrl, activeBranch)
            .updateTemplate(
                storylineState.id,
                getTemplateInputModel(storylineState.currentParagraph.templateName, templates)
            )
            .catch((error: api.ApiException) => {
                dispatch(showError(error.response));
            });
    }
}

export function setDatasourceData(datasourceName: string, newDataOrTransformer: ((oldData: unknown) => unknown) | unknown) {
    return (dispatch, getState: () => RootState) => {
        const storyline = getState().storyline;
        const existingDatasource = storyline.datasources.get(datasourceName);
        const existingDatasourceData = storyline.datasourceValues.get(datasourceName);
        const data = _.isFunction(newDataOrTransformer) ? newDataOrTransformer(existingDatasourceData) : newDataOrTransformer;

        if (!existingDatasource) {
            dispatch({
                type: ADD_GLOBAL_DATASOURCE,
                datasource: {
                    name: datasourceName,
                    defaultData: data,
                    parameters: []
                }
            } as AddGlobalDatasource);
        }

        dispatch({
            type: DATASOURCE_UPDATED,
            datasourceName,
            data
        } as DatasourceUpdatedAction);
    }
}

export function patchDatasourceData(datasourceName: string, diff: unknown) {
    return (dispatch, getState: () => RootState) => {
        const storyline = getState().storyline;
        const existingDatasource = storyline.datasources.get(datasourceName);

        if (!existingDatasource) {
            dispatch({
                type: ADD_GLOBAL_DATASOURCE,
                datasource: {
                    name: datasourceName,
                    defaultData: diff,
                    parameters: []
                }
            } as AddGlobalDatasource);
        }

        const existingDatasourceData = storyline.datasourceValues.get(datasourceName);

        dispatch({
            type: DATASOURCE_UPDATED,
            datasourceName,
            data: _.isObjectLike(existingDatasourceData) && !_.isDate(existingDatasourceData) ?
                _.merge(existingDatasourceData, diff) :
                diff
        } as DatasourceUpdatedAction);
    }
}